import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Alert, MedicalAlert, PatientAlert, AlertActionButtonsComponent, AlertActionButtonsService } from '@libs/shared-services';
import { SvgIconComponent, SvgIcons } from '@ngneat/svg-icon';
import { CdkAccordionItem } from '@angular/cdk/accordion';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Dialog } from '@angular/cdk/dialog';
import { HealthHistoryDialogComponent, HealthHistoryDialogData } from '@libs/home';
import { selectUserSiteId, patientActions } from '@libs/store';
import { routes } from '@libs/utils';
import { Store } from '@ngrx/store';

@Component({
  selector: 'lib-schedule-alert',
  templateUrl: './schedule-alert.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, SvgIconComponent, AlertActionButtonsComponent, CdkAccordionItem],
  animations: [
    trigger('slideToggle', [
      state('collapsed', style({ height: '0px', opacity: 0, overflow: 'hidden' })),
      state('expanded', style({ height: '*', opacity: 1, overflow: 'hidden' })),
      transition('collapsed <=> expanded', animate('300ms ease-in-out')),
    ]),
  ],
})
export class ScheduleAlertComponent {
  public readonly alert = input.required<Alert>();
  public readonly isExpanded = input(false);
  public readonly toggleExpanded = output<void>();
  public readonly clearExpandedIndex = output<void>();

  private readonly store = inject(Store);
  private readonly dialog = inject(Dialog);
  private readonly alertActionButtonsService = inject(AlertActionButtonsService);

  public readonly shouldShowAlert = signal(true);

  public readonly isMedicalAlert = computed(() => this.alert().type === 'medical');
  public readonly isPatientAlert = computed(() => this.alert().type === 'patient');
  public readonly medicalAlert = computed(() => this.alert() as MedicalAlert);
  public readonly patientAlert = computed(() => this.alert() as PatientAlert);
  public readonly iconColor = computed(() => (this.isMedicalAlert() ? 'text-states-deep-red' : 'text-states-orange'));
  public readonly titleColor = computed(() => (this.isMedicalAlert() ? 'text-states-deep-red-dark' : 'text-states-orange-dark'));
  public readonly iconKey = computed<SvgIcons>(() => (this.isMedicalAlert() ? 'px-16-medical-alert' : 'px-16-patient-alert'));

  constructor() {
    effect(() => {
      this.shouldShowAlert.set(this.alertActionButtonsService.shouldShowAlert(this.alert()));
    });
  }

  public onToggleExpanded(): void {
    this.toggleExpanded.emit();
  }

  public onHideAlert(): void {
    this.shouldShowAlert.set(false);
  }

  public onOpenHealthHistory(): void {
    const alert = this.alert();
    const siteId = this.store.selectSignal(selectUserSiteId)() ?? routes.showcaseSiteId;

    this.store.dispatch(patientActions.onOpenHealthHistory({ patientId: alert.patientId }));
    this.dialog.open<HealthHistoryDialogComponent, HealthHistoryDialogData>(HealthHistoryDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[1356px]', 'h-full', 'pt-[84px]', 'pb-[12px]'],
      data: {
        siteId,
        patientId: alert.patientId,
        activeTabIndex: this.isMedicalAlert() ? 0 : 2,
      },
    });
  }
}
